import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import classNames from 'classnames';
import { AppUser } from 'Src/views/App/App';
import { SchoolEvent, SchoolEventSchool } from 'Src/models/event';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { EVENT_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/event';
import { getAgesGroupFormatted, getEventStatus } from 'Src/helpers/accessor/accessor';
import {
  getPitchNameFromTournamentByPitchId,
  getSchoolEventLocation,
  getTournamentGroupNameByGroupId
} from 'Src/helpers/event/schoolEventViewHelper';
import { getEventParticipants, isClubEvent, isMultipartyEvent, isNonTeamSportEvent } from 'Src/helpers/event/event';
import { EventHeadToHeadFixture, hasEventResults } from 'Src/components/EventHeadToHeadFixture/EventHeadToHeadFixture';
import { EventChangeNotices } from 'Src/components/EventChangeNotices/EventChangeNotices';
import { EventMatchResultSummary } from 'Src/components/EventMatchResultSummary/EventMatchResultSummary';
import 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/SchoolEventMainInformation.scss';

interface Props {
  user: AppUser;
  event: SchoolEvent;
  eventName: string;
  activeSchoolId?: string;
  isPrevButtonDisabled?: boolean;
  isNextButtonDisabled?: boolean;
  onPrevEventClick?: () => void;
  onNextEventClick?: () => void;
  onReportAvailabilityClick?: () => void;
  onViewMapClick?: () => void;
}

const Icon = ({ type }: { type: 'calendar' | 'clock' | 'pin' | 'sport' | 'left' | 'right' | 'availability' }) => {
  const paths = {
    calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
    clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 7v5l3 2',
    pin: 'M12 22s7-6.13 7-12a7 7 0 1 0-14 0c0 5.87 7 12 7 12zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
    sport: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z',
    left: 'M19 12H5M11 18l-6-6 6-6',
    right: 'M5 12h14M13 6l6 6-6 6',
    availability: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[type]} />
    </svg>
  );
};

export const EventReadOnlyHero = ({
  user,
  event,
  eventName,
  activeSchoolId,
  isPrevButtonDisabled,
  isNextButtonDisabled,
  onPrevEventClick,
  onNextEventClick,
  onReportAvailabilityClick,
  onViewMapClick
}: Props) => {
  const { tournamentGroupId, quantumEventNumber, pitchId, startTime, endTime, sport, gender, tournament } = event;
  const tournamentName = propz.get(tournament, ['name'], '');
  const tournamentGroupName = getTournamentGroupNameByGroupId(
    tournamentGroupId,
    propz.get(tournament, ['customGroups'], [])
  );
  const pitchName = getPitchNameFromTournamentByPitchId(tournament || {}, pitchId);
  const ages = getAgesGroupFormatted(event, { user });
  const genderLabel = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[gender] || '';
  const status = getEventStatus(event);
  const isCancelled = status === EVENT_STATUS_SERVER_TO_CLIENT_MAPPING.CANCELED;
  const isFinished = status === EVENT_STATUS_SERVER_TO_CLIENT_MAPPING.FINISHED;
  const venue = getSchoolEventLocation(event, user);
  const specificLocation = propz.get(event, ['location'], '');
  const coordinates = propz.get(event, ['venue', 'postcodeData', 'point', 'coordinates']);
  const canOpenMap = Array.isArray(coordinates) && coordinates.length === 2 && typeof onViewMapClick === 'function';
  const eventSchools = [event.inviterSchool, ...(event.invitedSchools || [])].filter(Boolean) as SchoolEventSchool[];
  const participantUser = activeSchoolId ? { ...user, activeSchoolId } : user;
  const participants = getEventParticipants(event, participantUser, eventSchools as any);
  const isIndividual = isNonTeamSportEvent(event);
  const isMultiparty = isMultipartyEvent(event) || participants.length > 2;
  const showFixture = !isIndividual && !isMultiparty && participants.length === 2;
  const reasonForChange = String(propz.get(event, ['reasonForChange'], '') || '').trim();
  const reasonForCancellation = String(propz.get(event, ['reasonForCancellation'], '') || '').trim();

  return (
    <section className="eEventHero eEventHero--portal">
      <div className="eEventHero__top">
        <div className="eEventHero__identity">
          {showFixture ? (
            <EventHeadToHeadFixture event={event} participants={participants} />
          ) : (
            <div className="eEventHero__summary">
              <span className="eEventHero__sportIcon">
                <Icon type="sport" />
              </span>
              <div>
                <div className="eEventHero__summaryText">{sport?.name || 'Event'}</div>
                {hasEventResults(event) ? <div className="eEventHero__summaryText">Results available</div> : null}
              </div>
            </div>
          )}
        </div>
        <div className="eEventHero__controls">
          <span
            className={classNames('eEventHero__status', {
              'eEventHero__status--cancelled': isCancelled,
              'eEventHero__status--finished': isFinished
            })}
          >
            <i className="eEventHero__statusDot" aria-hidden="true" />
            {status}
          </span>
          {onReportAvailabilityClick ? (
            <button type="button" className="eEventHero__action" onClick={onReportAvailabilityClick}>
              <Icon type="availability" />
              Report availability
            </button>
          ) : null}
        </div>
      </div>

      <div className="eEventHero__meta">
        <span>
          <Icon type="calendar" />
          {moment(startTime).format('ddd D MMM YYYY')}
        </span>
        <span>
          <Icon type="clock" />
          {moment(startTime).format('HH:mm')}
          {endTime ? ` – ${moment(endTime).format('HH:mm')}` : ''}
        </span>
        <button
          type="button"
          className="eEventHero__location"
          disabled={!canOpenMap}
          onClick={canOpenMap ? onViewMapClick : undefined}
        >
          <Icon type="pin" />
          {isClubEvent(event) ? 'Location' : 'Venue'}: {venue || 'To be confirmed'}
          {specificLocation ? ` · ${specificLocation}` : ''}
          {canOpenMap ? ' · View map' : ''}
        </button>
        <span className="eEventHero__classification">
          <Icon type="sport" />
          {[sport?.name, ages, genderLabel].filter(Boolean).map(value => (
            <span className="eEventHero__classificationChip" key={String(value)}>
              {value}
            </span>
          ))}
        </span>
        {(onPrevEventClick || onNextEventClick) && (
          <span className="eEventHero__metaNavigation">
            <button
              type="button"
              aria-label="Previous event"
              disabled={isPrevButtonDisabled}
              onClick={onPrevEventClick}
            >
              <Icon type="left" />
            </button>
            <button type="button" aria-label="Next event" disabled={isNextButtonDisabled} onClick={onNextEventClick}>
              <Icon type="right" />
            </button>
          </span>
        )}
      </div>

      {(tournamentName || tournamentGroupName || quantumEventNumber || pitchName) && (
        <div className="eEventHero__secondaryMeta">
          {tournamentName ? (
            <span>
              Tournament: <strong>{tournamentName}</strong>
            </span>
          ) : null}
          {tournamentGroupName ? (
            <span>
              Group: <strong>{tournamentGroupName}</strong>
            </span>
          ) : null}
          {pitchName ? (
            <span>
              Pitch: <strong>{pitchName}</strong>
            </span>
          ) : null}
          {quantumEventNumber ? (
            <span>
              Quantum: <strong>{quantumEventNumber}</strong>
            </span>
          ) : null}
        </div>
      )}
      <EventChangeNotices
        latestChange={reasonForChange}
        cancellationReason={isCancelled ? reasonForCancellation : ''}
      />
      {event.resultPublication?.status !== 'DRAFT' ? <EventMatchResultSummary event={event} /> : null}
    </section>
  );
};

EventReadOnlyHero.displayName = 'EventReadOnlyHero';
